import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Navigation from './Navigation';
import Footer from './Footer';

const Privacy = () => {
  return (
    <PageContainer>
      <Navigation />
      <Section>
        <ContentWrapper>
          <PagePill>Data Protection</PagePill>

          <MainHeading>Privacy Policy</MainHeading>

          <ContentSection>
            <SectionHeading>1. Overview</SectionHeading>
            <InfoText>
              This Privacy Policy explains how Sonor AI (operated by Mohamed Elgazzar, "we", "us", or "our") collects, uses, and protects your personal data when you visit our website at sonorai.co. We are committed to ensuring the privacy and protection of your personal information in compliance with the General Data Protection Regulation (GDPR) and other applicable data protection laws.
            </InfoText>
          </ContentSection>

          <ContentSection>
            <SectionHeading>2. Data Controller</SectionHeading>
            <InfoText>
              The data controller responsible for the processing of your personal data is:<br /><br />
              Mohamed Elgazzar<br />
              Quartiersweg 6<br />
              10829 Berlin<br />
              Germany<br /><br />
              Email: hello@sonorai.co
            </InfoText>
          </ContentSection>

          <ContentSection>
            <SectionHeading>3. Data We Collect</SectionHeading>
            <InfoText>
              When you visit our website, we may collect the following types of information:
            </InfoText>
            <SubHeading>3.1 Information Collected Automatically</SubHeading>
            <InfoText>
              <ul>
                <li>IP address</li>
                <li>Browser type and version</li>
                <li>Operating system</li>
                <li>Date and time of your visit</li>
                <li>Pages you visit</li>
                <li>Referring website</li>
              </ul>
            </InfoText>
            <SubHeading>3.2 Information You Provide</SubHeading>
            <InfoText>
              When you interact with our website, particularly when you use our booking feature that links to Cal.com, you may provide personal information such as:
              <ul>
                <li>Name</li>
                <li>Email address</li>
                <li>Phone number</li>
                <li>Company information</li>
                <li>Any other information you choose to provide in communication with us</li>
              </ul>
            </InfoText>
          </ContentSection>

          <ContentSection>
            <SectionHeading>4. Legal Basis for Processing</SectionHeading>
            <InfoText>
              We process your personal data based on the following legal grounds:
              <ul>
                <li><strong>Legitimate Interest (Art. 6(1)(f) GDPR):</strong> We process certain data based on our legitimate interest in improving our website, understanding user behavior, and providing our services.</li>
                <li><strong>Consent (Art. 6(1)(a) GDPR):</strong> Where required by law, we will only process your data with your explicit consent.</li>
                <li><strong>Contract Performance (Art. 6(1)(b) GDPR):</strong> If you request our services, we may process your data to fulfill our contractual obligations.</li>
                <li><strong>Legal Obligation (Art. 6(1)(c) GDPR):</strong> We may process your data to comply with legal requirements.</li>
              </ul>
            </InfoText>
          </ContentSection>

          <ContentSection>
            <SectionHeading>5. External Services and Data Transfers</SectionHeading>
            <InfoText>
              Our website uses external services that may process your data:
            </InfoText>
            <SubHeading>5.1 Cal.com</SubHeading>
            <InfoText>
              We use Cal.com for scheduling appointments. When you book a call through our website, you will be redirected to Cal.com, which has its own privacy policy. We recommend reviewing their privacy policy to understand how they process your data.
            </InfoText>
            <SubHeading>5.2 Future Analytics</SubHeading>
            <InfoText>
              We may implement analytics tools in the future to help us understand how users interact with our website. If implemented, we will update this privacy policy accordingly.
            </InfoText>
          </ContentSection>

          <ContentSection>
            <SectionHeading>6. Cookies and Similar Technologies</SectionHeading>
            <InfoText>
              Currently, our website does not use cookies for tracking purposes. However, we may implement cookies in the future to enhance user experience and provide better services. If we do implement cookies, we will update this privacy policy and provide appropriate notice and consent mechanisms in accordance with applicable laws.
            </InfoText>
          </ContentSection>

          <ContentSection>
            <SectionHeading>7. Your Rights Under GDPR</SectionHeading>
            <InfoText>
              Under the GDPR, you have the following rights regarding your personal data:
              <ul>
                <li><strong>Right to Access (Art. 15 GDPR):</strong> You have the right to request information about whether we process your personal data and to access that data.</li>
                <li><strong>Right to Rectification (Art. 16 GDPR):</strong> You have the right to request the correction of inaccurate personal data or to complete incomplete data.</li>
                <li><strong>Right to Erasure (Art. 17 GDPR):</strong> You have the right to request the deletion of your personal data under certain circumstances.</li>
                <li><strong>Right to Restriction of Processing (Art. 18 GDPR):</strong> You have the right to request the restriction of processing your personal data under certain conditions.</li>
                <li><strong>Right to Data Portability (Art. 20 GDPR):</strong> You have the right to receive your personal data in a structured, commonly used, and machine-readable format.</li>
                <li><strong>Right to Object (Art. 21 GDPR):</strong> You have the right to object to the processing of your personal data under certain circumstances.</li>
                <li><strong>Right to Withdraw Consent (Art. 7(3) GDPR):</strong> If processing is based on consent, you have the right to withdraw your consent at any time.</li>
              </ul>
            </InfoText>
            <InfoText>
              To exercise any of these rights, please contact us at hello@sonorai.co. We will respond to your request within one month.
            </InfoText>
          </ContentSection>

          <ContentSection>
            <SectionHeading>8. Data Security</SectionHeading>
            <InfoText>
              We implement appropriate technical and organizational measures to protect your personal data against accidental or unlawful destruction, loss, alteration, unauthorized disclosure, or access. However, please note that no method of transmission over the Internet or method of electronic storage is 100% secure.
            </InfoText>
          </ContentSection>

          <ContentSection>
            <SectionHeading>9. Data Retention</SectionHeading>
            <InfoText>
              We retain your personal data only for as long as necessary to fulfill the purposes for which it was collected, including for the purposes of satisfying any legal, accounting, or reporting requirements.
            </InfoText>
          </ContentSection>

          <ContentSection>
            <SectionHeading>10. Changes to This Privacy Policy</SectionHeading>
            <InfoText>
              We may update this Privacy Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "last updated" date.
            </InfoText>
          </ContentSection>

          <ContentSection>
            <SectionHeading>11. Contact Us</SectionHeading>
            <InfoText>
              If you have any questions about this Privacy Policy or our data practices, please contact us at:<br /><br />
              Email: hello@sonorai.co<br />
              Address: Quartiersweg 6, 10829 Berlin, Germany
            </InfoText>
          </ContentSection>

          <LastUpdated>Last updated: April 2025</LastUpdated>
        </ContentWrapper>
      </Section>
      <Footer />
    </PageContainer>
  );
};

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  overflow-x: hidden;
  background-color: var(--color-dark-600);
  background-image: radial-gradient(rgba(255, 255, 255, 0.08) 1px, transparent 1px);
  background-size: 20px 20px;
`;

const Section = styled.section`
  padding: 160px 20px 100px;
  max-width: 1200px;
  margin: 0 auto;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 800px;
  margin: 0 auto;
`;

const PagePill = styled.div`
  display: inline-block;
  padding: 8px 20px;
  background-color: rgba(17, 17, 17, 0.9);
  border-radius: 50px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 30px;
  align-self: flex-start;
`;

const MainHeading = styled.h1`
  font-size: 38px;
  font-weight: 700;
  color: white;
  margin-bottom: 40px;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 32px;
  }

  @media (max-width: 480px) {
    font-size: 28px;
  }
`;

const ContentSection = styled.div`
  margin-bottom: 40px;
`;

const SectionHeading = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: var(--color-ui-orange);
  margin-bottom: 16px;

  @media (max-width: 768px) {
    font-size: 22px;
  }

  @media (max-width: 480px) {
    font-size: 20px;
  }
`;

const SubHeading = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: white;
  margin-bottom: 12px;
  margin-top: 20px;
`;

const InfoText = styled.div`
  font-size: 16px;
  line-height: 1.6;
  color: var(--color-text-light);
  margin-bottom: 16px;

  ul {
    padding-left: 20px;
    margin: 10px 0;
  }

  li {
    margin-bottom: 8px;
  }
`;

const LastUpdated = styled.p`
  font-size: 14px;
  color: var(--color-text-gray);
  margin-top: 40px;
  font-style: italic;
`;

export default Privacy;
